import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ListTableComponent, PopupDialogComponent } from '@ppl/ui';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { NgZorroModule } from '../../../../../shared/modules/ng-zorro/ng-zorro.module';
import { TableRowActions } from '../../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../../shared/interfaces/list.interface';
import { ENDPOINTS } from '../../../../../shared/constants/url.constants';
import { PAGE_CONSTANTS } from '../../../../../shared/constants/page.constants';
import { AllowListAuthStore } from '../../../../../shared/authorization/allow-list-auth.store';
import { DialogConfig, DialogService } from '../../../../../shared/services/dialog.service';
import { ErrorAlertService } from '../../../../../shared/services/error-alert.service';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { SharedDataService } from '../../../../../shared/services/shared-data-service';
import { DateTimeFormatterService } from '../../../../../shared/utils/date-time-formatter.service';
import { LookupService } from '../../../../../services/lookup.service';
import { UserService } from '../../../../../services/user.service';
import { InvoiceService } from '../../../../../services/invoice.service';
import { TimesheetService } from '../../../../../services/timesheet.service';
import { correctionResults, PayrollCorrectionService } from '../../../../../services/payroll-correction.service';
import { InvoiceFilterComponent } from '../invoices-staff-list/invoice-filter/invoice-filter.component';
import { CorrectionNotesDialogComponent } from '../../../corrections/correction-notes-dialog/correction-notes-dialog.component';
import { notifyCorrectionResults } from '../../../corrections/correction-lines';

/** Pended invoice lines: Override / Deny (selected or all matching), Submit For Processing and Reprocess. */
@Component({
  selector: 'ppl-invoice-pended-list',
  standalone: true,
  imports: [
    CommonModule,
    ListTableComponent,
    PopupDialogComponent,
    NgZorroModule,
    NzFormDirective,
    FormsModule,
    ReactiveFormsModule,
    InvoiceFilterComponent,
    CorrectionNotesDialogComponent,
  ],
  templateUrl: './invoice-pended-list.component.html',
  styleUrl: './invoice-pended-list.component.css'
})
export class InvoicePendedListComponent implements OnInit {
  pageId: string = PAGE_CONSTANTS.STAFF_PENDED_INVOICES;
  private readonly PAGE_NAME = 'pendManagementInvoiceList';
  private readonly PEND_CODES_PAGE_NAME = 'pendCodesInvoice';
  /** Processing statuses (Denied, Ready for Payment) that can be reprocessed if not yet sent to NAV. */
  private readonly REPROCESSABLE_STATUSES = ['D', 'RP'];

  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  @ViewChild(InvoiceFilterComponent) invoiceFilterChildComponent!: InvoiceFilterComponent;
  @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;

  private dialogSvc = inject(DialogService);

  actionButtons: TableRowActionsModel = new TableRowActions({ view: false });
  appliedFilters: any;
  programName = '';
  processingStatusList: any;
  reasonList: any;
  businessStatusList: any;
  invoices: any[] = [];
  hasSearched = false;
  title = '';
  okText = '';
  showActionPopup = false;
  buttonActionEvent: any;
  selectedCheckboxes: any;
  processCodes: any[] = [];
  pendCodes: any[] = [];
  allowedProcessCodes: any[] = [];
  invoiceActionForm!: FormGroup;
  reprocessIds: number[] = [];
  showReprocessPopup = false;
  isPageRestrictionLoaded = false;
  isReadOnly = false;

  // Processing status is shown from processStatusText: ppl-list-table renders processStatus as a hyperlink.
  cols = [
    { field: 'payPeriod', header: 'Pay \nPeriod', jsonpath: 'payPeriod', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'serviceDate', header: 'Service \nDate', jsonpath: 'serviceDate', isDisplayedAtHeader: 'y', isSearchable: 'y' },
    { field: 'createdDate', header: 'Submission \nDate', jsonpath: 'createdDate', isDisplayedAtHeader: 'y', isSearchable: 'y' },
    { field: 'consumerName', header: 'Participant \nName', jsonpath: 'consumerName', isDisplayedAtHeader: 'y', isSearchable: 'y' },
    { field: 'consumerPPLId', header: 'Participant \nPPL ID', jsonpath: 'consumerPPLId', isDisplayedAtHeader: 'y', isSearchable: 'n', isClickable: true },
    { field: 'providerName', header: 'Provider \nName', jsonpath: 'providerName', isDisplayedAtHeader: 'y', isSearchable: 'y' },
    { field: 'providerPPLId', header: 'Provider PPL ID', jsonpath: 'providerPPLId', isDisplayedAtHeader: 'y', isSearchable: 'n', isClickable: true },
    { field: 'applicationStatus', header: 'Application \nStatus', jsonpath: 'applicationStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'processStatusText', header: 'Processing \nStatus', jsonpath: 'processStatusText', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'serviceDesc', header: 'Service \nDescription', jsonpath: 'serviceDesc', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'billedUnit', header: 'Billed \nUnits', jsonpath: 'billedUnit', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'billRate', header: 'Service \nRate', jsonpath: 'billRate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];

  buttons = [
    { button_id: 'Override', button_label: 'Override', buttonAction: 'overrideInvoice', className: 'primary-btn-color' },
    { button_id: 'OverrideAll', button_label: 'Override All', buttonAction: 'overrideAllInvoices', className: 'primary-btn-color' },
    { button_id: 'Deny', button_label: 'Deny', buttonAction: 'denyInvoice', className: 'primary-btn-color' },
    { button_id: 'DenyAll', button_label: 'Deny All', buttonAction: 'denyAllInvoices', className: 'primary-btn-color' },
    { button_id: 'submitForProcessing', button_label: 'Submit For Processing', buttonAction: 'submitForProcessing', className: 'primary-btn-color' },
    { button_id: 'Reprocess', button_label: 'Reprocess', buttonAction: 'reprocessInvoice', className: 'primary-btn-color' },
  ];

  fields = [
    { field: 'Timeframe', show: true, default: '30 days' },
    { field: 'consumer', show: true },
    { field: 'consumerPplId', show: true },
    { field: 'provider', show: true },
    { field: 'providerPplId', show: true },
    { field: 'payPeriod', show: true, hideFuturePeriods: true },
    { field: 'processStatusLkup', show: true, default: 'PE' },
    { field: 'serviceCode', show: true },
    { field: 'processCode', show: true },
    { field: 'serviceDate', show: true },
    { field: 'status', show: true }
  ];

  keyMapping: { [key: string]: string } = {
    Timeframe: 'serviceDuration',
    payPeriod: 'serviceId',
    consumer: 'consumerName',
    provider: 'providerName',
    providerPplId: 'providerPPLId',
    consumerPplId: 'consumerPPLId',
    processStatusLkup: 'processStatus',
    paymentDate: 'paymentId'
  };

  onViewUserClicked = (payload: any) => {
    this.userService.setViewUserEvent({ userId: payload.userId, isConsumer: payload.isConsumer, showAllTabs: true });
  }

  constructor(
    private fb: FormBuilder,
    private lookupService: LookupService,
    private dateTimeFormatterService: DateTimeFormatterService,
    private invoiceService: InvoiceService,
    private timesheetService: TimesheetService,
    private payrollCorrectionService: PayrollCorrectionService,
    private errorAlertService: ErrorAlertService,
    private notificationService: NotificationService,
    private sharedDataService: SharedDataService,
    private userService: UserService,
    private allowListStore: AllowListAuthStore
  ) { }

  ngOnInit(): void {
    this.invoiceActionForm = this.fb.group({
      selectedProcessCode: [null, [Validators.required]],
      reason: [null, [Validators.required]],
      comments: [null, [Validators.required, Validators.maxLength(10000)]],
    });

    this.programName = this.userService.getSelectedProgram()?.program_code ?? '';
    this.businessStatusList = this.lookupService.getLookupValue('Business Status');
    this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
    this.reasonList = this.lookupService.getLookupValue('Disposition Reason');
    this.setPageAccess();
    this.getProcessCodes();
  }

  private setPageAccess() {
    const pageConfig = this.allowListStore.getConfig()?.pages[this.pageId];
    if (pageConfig) {
      const allowedActions = this.allowListStore.getAllowedActions(this.pageId);
      const allowedButtons = this.allowListStore.getAllowedButtons(this.pageId);
      this.isReadOnly = allowedActions.length === 0 && allowedButtons.length === 0;
    } else {
      this.isReadOnly = true;
    }
    this.isPageRestrictionLoaded = true;
  }

  // TODO: confirm invoices share the timesheet pend-management process codes (used by the
  // Process Code filter and by Override All / Deny All).
  getProcessCodes() {
    this.timesheetService.getProcessCodes(this.programName).subscribe((data: any) => {
      this.processCodes = this.sharedDataService.sortByKey(data.responsedata, 'code');
      this.processCodes.forEach((processCode: any) => {
        processCode.displayName = `${processCode.code.trim()} - ${processCode.desc.trim()}`;
      });
    });
  }

  buildRequestPayload(pageName: string, orderBy: string, searchParams: any[], startingPageNumber = 1, recordsPerPage = 20) {
    return {
      pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage,
      startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };
  }

  searchInvoices(filters: any, startingPageNumber = 1, recordsPerPage = 20) {
    if (!filters?.length) return;
    this.hasSearched = true;
    const payload = this.buildRequestPayload(this.PAGE_NAME, 'invoiceLineId', filters, startingPageNumber, recordsPerPage);
    this.invoiceService.getListPageData(payload).subscribe((data: any) => {
      const records = this.processData(data?.responsedata ?? []);
      this.invoices = startingPageNumber > 1 ? [...this.invoices, ...records] : records;
    });
  }

  processData(data: any[]) {
    data.forEach((element: any) => {
      element.payPeriod = element.payrollStart + ' - ' + element.payrollEnd;
      const aStatus = this.businessStatusList.find((status: any) => status.lkpValueCode === element.businessStatus);
      element.applicationStatus = aStatus ? aStatus.lkpValueName : null;
      element.processingStatusLkp = element.processStatus;
      const pStatus = this.processingStatusList.find((status: any) => status.lkpValueCode === element.processStatus);
      element.processStatusText = pStatus ? pStatus.lkpValueName : element.processStatus;
    });
    return data;
  }

  loadRecordsAfterUpdate() {
    this.clearGridSelection();
    let filters = this.appliedFilters;
    if (!filters?.length) {
      filters = [
        { jsonPath: 'serviceDuration', value: '30 days', operator: 'lessThan' },
        { jsonPath: 'processStatus', value: 'PE', operator: 'Equal' },
      ];
    }
    this.searchInvoices(filters, undefined, this.childComponent?.recordsPerPage ?? 20);
  }

  onPageLoad(event: any) {
    this.searchInvoices(this.appliedFilters, event.startingPageNumber, event.rows ?? 20);
  }

  onSearch(event: any) {
    const filters: any[] = [{ jsonPath: 'serviceDuration', value: event.Timeframe ? event.Timeframe : '30 days', operator: 'lessThan' }];
    if (event.serviceDate) {
      filters.push({ jsonPath: 'serviceDate', value: this.dateTimeFormatterService.formatDateToString(event.serviceDate as Date), operator: 'Equal' });
    }
    if (event.payPeriod) {
      filters.push({ jsonPath: 'serviceId', value: event.payPeriod, operator: 'Equal' });
    }
    if (event.consumer?.trim()) {
      filters.push({ jsonPath: 'consumerName', value: event.consumer.trim(), operator: 'Contains' });
    }
    if (event.provider?.trim()) {
      filters.push({ jsonPath: 'providerName', value: event.provider.trim(), operator: 'Contains' });
    }
    if (event.providerPplId?.trim()) {
      filters.push({ jsonPath: 'providerPPLId', value: event.providerPplId.trim(), operator: 'Equal' });
    }
    if (event.consumerPplId?.trim()) {
      filters.push({ jsonPath: 'consumerPPLId', value: event.consumerPplId.trim(), operator: 'Equal' });
    }
    if (event.status) {
      filters.push({ jsonPath: 'businessStatus', value: event.status, operator: 'Equal' });
    }
    if (event.processStatusLkup) {
      filters.push({ jsonPath: 'processStatus', value: event.processStatusLkup, operator: 'Equal' });
    }
    if (event.serviceCode) {
      filters.push({ jsonPath: 'serviceCode', value: event.serviceCode, operator: 'Equal' });
    }
    if (event.processCode) {
      filters.push({ jsonPath: 'processCode', value: event.processCode, operator: 'Equal' });
    }

    this.appliedFilters = filters;
    this.searchInvoices(filters, undefined, this.childComponent?.recordsPerPage ?? 20);
  }

  isFilterValid(filterValues: any, appliedFilters: any[]) {
    return Object.entries(filterValues)
      .filter(([_, value]) => value !== null && value !== '')
      .every(([key, value]) => {
        const mappedKey = this.keyMapping[key] || key;
        const comparisonValue = mappedKey === 'serviceDate' ? this.dateTimeFormatterService.formatDateToString(value as Date) : value;
        return (appliedFilters ?? []).some((filter: any) => filter.jsonPath === mappedKey && filter.value === comparisonValue);
      });
  }

  gridAction(event: any) {
    this.buttonActionEvent = event.button;
    this.selectedCheckboxes = event.selectedCheckBox ?? [];
    this.invoiceActionForm.reset();

    if (!this.isFilterValid(this.invoiceFilterChildComponent.invoiceSearchForm.value, this.appliedFilters)) {
      this.errorAlertService.openModal('error', 'Error',
        'Some filters are newly applied but the search is not performed. Kindly perform search with updated filter or remove the filter.');
      return;
    }

    const action = event.button.buttonAction;
    if (['denyAllInvoices', 'overrideAllInvoices'].includes(action)) {
      if (!this.validateAllActionFilters()) return;
      this.openConfirmationPopup(event);
      return;
    }

    if (!this.selectedCheckboxes.length) {
      this.errorAlertService.openModal('error', 'Error', 'Please select records for completing this action.');
      return;
    }
    if (action === 'submitForProcessing') {
      this.handleSubmitForProcessing();
    } else if (action === 'reprocessInvoice') {
      this.handleReprocess();
    } else {
      this.handlePendCodes(this.selectedCheckboxes.map((e: any) => e.invoiceLineId), event);
    }
  }

  /** Override All / Deny All act on every line matching the search, so the search must be narrowed first. */
  private validateAllActionFilters(): boolean {
    const filterJsonPaths = (this.appliedFilters ?? []).map((entry: any) => entry.jsonPath);
    const hasBaseFiltersApplied = filterJsonPaths.includes('serviceDuration') && filterJsonPaths.includes('processStatus');
    const hasUserFiltersApplied = ['consumerName', 'consumerPPLId', 'providerName', 'providerPPLId'].some(p => filterJsonPaths.includes(p));
    const optionalFiltersApplied = ['serviceId', 'paymentId', 'serviceCode', 'processCode'].some(p => filterJsonPaths.includes(p));

    if (!hasBaseFiltersApplied) {
      this.errorAlertService.openModal('error', 'Error', 'Please select the Service Duration and Process Status to continue with this action.');
      return false;
    }
    if (!hasUserFiltersApplied && !optionalFiltersApplied) {
      this.errorAlertService.openModal('error', 'Error',
        'Please select at least one filter (Consumer, Provider, Service ID, or Payment ID) to continue with this action.');
      return false;
    }
    return true;
  }

  handleSubmitForProcessing() {
    const invalidSelected = this.selectedCheckboxes.filter((e: any) => !['PE', 'SB', 'SE'].includes(e.processingStatusLkp));
    if (invalidSelected.length > 0) {
      this.errorAlertService.openModal('error', 'Error', 'Please select only Pending or Submitted or System Error invoices.');
      return;
    }
    const payload = { invoicelineDetailIds: this.selectedCheckboxes.map((item: any) => item.invoiceLineId) };
    this.invoiceService.submitAdjudicationDetails(payload, this.programName).subscribe((data: any) => {
      if (data.status === 200) {
        this.loadRecordsAfterUpdate();
      }
    });
  }

  handleReprocess() {
    if (this.selectedCheckboxes.some((e: any) => !this.REPROCESSABLE_STATUSES.includes(e.processingStatusLkp))) {
      this.errorAlertService.openModal('error', 'Error', 'Please select only Denied or Ready for Payment invoices to reprocess.');
      return;
    }
    this.reprocessIds = this.selectedCheckboxes.map((e: any) => e.invoiceLineId);
    this.showReprocessPopup = true;
  }

  submitReprocess(notes: string) {
    this.payrollCorrectionService.reprocess(this.programName, 'IV', notes, this.reprocessIds).subscribe((data: any) => {
      this.showReprocessPopup = false;
      notifyCorrectionResults(this.notificationService, correctionResults(data), 'reprocessed', 'invoice line(s)');
      this.loadRecordsAfterUpdate();
    });
  }

  handlePendCodes(invoiceLineIds: number[], event: any) {
    const filters = [{ jsonPath: 'invoiceLineId', values: invoiceLineIds, operator: 'IN' }];
    const payload = this.buildRequestPayload(this.PEND_CODES_PAGE_NAME, 'codeCount', filters, 1, 50);
    this.invoiceService.getListPageData(payload).subscribe((data: any) => {
      this.pendCodes = this.sharedDataService.sortByKey(data?.responsedata ?? [], 'code');
      this.pendCodes.forEach((pendCode: any) => {
        pendCode.displayName = `${pendCode.code.trim()} - ${pendCode.externalMessage?.trim() ?? ''}`;
      });
      this.processDataForModel(event);
    });
  }

  processDataForModel(event: any) {
    const action = event.button.buttonAction;
    if (action === 'overrideInvoice') {
      this.allowedProcessCodes = this.pendCodes.filter(e => e.allowOverride);
    } else if (action === 'overrideAllInvoices') {
      this.allowedProcessCodes = this.processCodes.filter(e => e.allowOverride);
    } else if (action === 'denyAllInvoices') {
      this.allowedProcessCodes = this.processCodes;
    } else {
      this.allowedProcessCodes = this.pendCodes;
    }

    const isDeny = action.toLowerCase().includes('deny');
    this.reasonList = this.lookupService.getLookupValue(isDeny ? 'Deny Reason' : 'Disposition Reason');
    this.title = event.button.button_label;
    this.okText = event.button.button_label;
    this.showActionPopup = true;
  }

  openConfirmationPopup(event: any) {
    const dialogConfig: DialogConfig = {
      title: 'Confirmation',
      okText: 'Confirm',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: true,
    };
    this.pendCodes = [];
    this.dialogSvc.openDialog(this.confirmationDialog, dialogConfig, () => this.processDataForModel(event), () => this.handleCancel());
  }

  handleCancel() {
    this.showActionPopup = false;
  }

  handleOk() {
    if (this.invoiceActionForm.invalid) {
      this.invoiceActionForm.markAllAsTouched();
      return;
    }

    const formValues = this.invoiceActionForm.value;
    const codeSource = this.pendCodes?.length ? this.pendCodes : this.processCodes;
    const payload: any = {
      processCode: (formValues.selectedProcessCode ?? []).flatMap((code: string) => {
        const match = codeSource.find(e => e.code == code);
        return match ? [{ code: match.processCodeId || match.processCodeDetailId, reasonLkp: formValues.reason, comments: formValues.comments }] : [];
      }),
    };

    let url = '';
    let placeholder = '';
    switch (this.buttonActionEvent.buttonAction) {
      case 'denyInvoice':
        url = ENDPOINTS.INVOICE.PEND_DENY;
        placeholder = 'denied';
        payload.invoiceLine = this.selectedCheckboxes.map((value: any) => value.invoiceLineId);
        break;
      case 'overrideInvoice':
        url = ENDPOINTS.INVOICE.PEND_OVERRIDE;
        placeholder = 'overridden';
        payload.invoiceLine = this.selectedCheckboxes.map((value: any) => value.invoiceLineId);
        break;
      case 'denyAllInvoices':
        url = ENDPOINTS.INVOICE.PEND_DENY_ALL;
        placeholder = 'denied';
        payload.searchParam = this.appliedFilters;
        break;
      case 'overrideAllInvoices':
        url = ENDPOINTS.INVOICE.PEND_OVERRIDE_ALL;
        placeholder = 'overridden';
        payload.searchParam = this.appliedFilters;
        break;
    }

    this.invoiceService.pendManagementAction(url, this.programName, payload, placeholder).subscribe(() => {
      this.showActionPopup = false;
      this.loadRecordsAfterUpdate();
    });
  }

  /** Reloading doesn't reset ppl-list-table's selection, so clear it to avoid resending the same lines. */
  private clearGridSelection(): void {
    this.selectedCheckboxes = [];
    if (this.childComponent?.selectedCheckBoxValue) {
      this.childComponent.selectedCheckBoxValue = [];
    }
  }
}
